/**
 * Collapsible Sidebar Navigation
 * 
 * Hamburger menu that expands to a full sidebar.
 * Great for minimal designs and mobile-first experiences.
 */

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Menu, Calendar, Users, Heart, X, ChevronRight } from "lucide-react";
import { useNavigationItems } from "@/hooks/useNavigationItems";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { cn } from "@/lib/utils";

const NavigationCollapsible = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { features, navigation } = useSiteSettings();
  const config = useSiteConfig();
  const { navigationItems: navItems } = useNavigationItems();

  const isActive = (path: string) => location.pathname === path;

  const handleNavigate = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Fixed Header Bar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border h-16 flex items-center px-4">
        <div className="flex items-center justify-between w-full max-w-7xl mx-auto">
          {/* Hamburger Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 bg-primary text-primary-foreground">
              <SheetHeader className="p-6 border-b border-primary-foreground/10">
                <SheetTitle className="text-primary-foreground flex items-center space-x-3">
                  <span className="truncate">{config.organizationName}</span>
                </SheetTitle>
              </SheetHeader>

              <ScrollArea className="flex-1 h-[calc(100vh-180px)]">
                <nav className="p-4 space-y-1">
                  {navItems.map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={handleNavigate}
                      className={cn(
                        "flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all",
                        isActive(item.path)
                          ? "bg-heritage text-heritage-foreground"
                          : "text-primary-foreground/80 hover:bg-primary-foreground/10 hover:text-primary-foreground"
                      )}
                    >
                      <span>{item.name}</span>
                      {isActive(item.path) && <ChevronRight className="w-4 h-4" />}
                    </Link>
                  ))}
                </nav>

                {/* Divider */}
                <div className="mx-4 my-4 border-t border-primary-foreground/10" />

                {/* CTA Section */}
                <div className="px-4 space-y-2">
                  {features.events.enabled && navigation.showEventsButton && (
                    <Button 
                      variant="culture" 
                      className="w-full justify-start" 
                      asChild
                      onClick={handleNavigate}
                    >
                      <Link to="/events">
                        <Calendar className="w-4 h-4 mr-2" />
                        {features.events.buttonText}
                      </Link>
                    </Button>
                  )}
                  {features.membership.enabled && (
                    <Button 
                      variant="heritage" 
                      className="w-full justify-start" 
                      asChild
                      onClick={handleNavigate}
                    >
                      <Link to="/membership">
                        <Users className="w-4 h-4 mr-2" />
                        {features.membership.buttonText}
                      </Link>
                    </Button>
                  )}
                  {features.donation.enabled && (
                    <Button 
                      variant="donate" 
                      className="w-full justify-start" 
                      asChild
                      onClick={handleNavigate}
                    >
                      <Link to="/donate">
                        <Heart className="w-4 h-4 mr-2" />
                        {features.donation.buttonText}
                      </Link>
                    </Button>
                  )}
                </div>
              </ScrollArea>

              {/* Footer */}
              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-primary-foreground/10 bg-primary">
                <p className="text-xs text-primary-foreground/60 text-center">
                  © {new Date().getFullYear()} {config.organizationName}
                </p>
              </div>
            </SheetContent>
          </Sheet>

          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            {config.logoUrl && config.logoUrl.trim() !== '' && (
              <img
                src={config.logoUrl}
                alt={config.organizationName}
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  console.error('[NavigationCollapsible] Failed to load logo:', config.logoUrl);
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <span className="text-xl font-bold text-foreground">
              {config.organizationName}
            </span>
          </Link>

          {/* Right side actions (optional) */}
          <div className="w-10" /> {/* Spacer for balance */}
        </div>
      </header>

      {/* Spacer to prevent content from going under fixed header */}
      <div className="h-16" />
    </>
  );
};

export default NavigationCollapsible;


